import { ApiProperty, ApiPropertyOptional } from '@nestjs/swagger';

/**
 * DTO para item de DUS (tblDusItems)
 * Representa cada producto/ítem dentro de un DUS
 */
export class ControlDusItemDetailDto {
  @ApiProperty({ description: 'ID único del item', example: 63536 })
  id!: number;

  @ApiProperty({ description: 'Secuencia del item', example: 1 })
  sequenceId!: number;

  @ApiPropertyOptional({
    description: 'Código arancelario',
    example: '08052100',
  })
  harmonizedSystemCode?: string;

  @ApiPropertyOptional({
    description: 'Descriptor del producto',
    example: 'MANDARINAS FRESCAS',
  })
  descriptor?: string;

  @ApiProperty({ description: 'Peso neto por unidad (KG)', example: 15.6 })
  netWeigth!: number;

  @ApiProperty({ description: 'Peso bruto por unidad (KG)', example: 16.7 })
  grossWeigth!: number;

  @ApiProperty({ description: 'Cantidad de unidades', example: 1300 })
  quantity!: number;

  @ApiProperty({ description: 'Peso neto total (KG)', example: 20280 })
  netTotalWeigth!: number;

  @ApiProperty({ description: 'Peso bruto total (KG)', example: 21710 })
  grossTotalWeigth!: number;

  @ApiProperty({ description: 'Valor FOB total (USD)', example: 26650 })
  totalFOBValue!: number;

  @ApiPropertyOptional({ description: 'Valor FOB unitario', example: 1.314103 })
  unitFOBValue?: number;

  @ApiPropertyOptional({
    description: 'Marca del producto',
    example: 'EXSER-F',
  })
  brand?: string;

  @ApiPropertyOptional({
    description: 'Nombre del tipo de envase',
    example: 'CARTON',
  })
  typeOfPackageName?: string;
}

/**
 * DTO para bulto/paquete de DUS (tblDusPackages)
 * Representa los bultos o contenedores dentro del DUS
 */
export class ControlDusPackageDto {
  @ApiProperty({ description: 'ID único del bulto', example: 13620 })
  id!: number;

  @ApiProperty({ description: 'Secuencia del bulto', example: 1 })
  sequenceId!: number;

  @ApiPropertyOptional({
    description: 'Nombre del tipo de bulto',
    example: 'TRAY',
  })
  typeOfPackageName?: string;

  @ApiProperty({ description: 'Cantidad de bultos', example: 1 })
  quantity!: number;

  @ApiPropertyOptional({
    description: 'Identificación del bulto',
    example: 'ABC123',
  })
  identification?: string;

  @ApiPropertyOptional({
    description: 'Subcontenido',
    example: '1300 CAJAS DE CARTÓN (22)',
  })
  subcontains?: string;
}

/**
 * DTO para contenedor de DUS (tblDusContainers)
 */
export class ControlDusContainerDto {
  @ApiProperty({ description: 'ID único del contenedor', example: 1234 })
  id!: number;

  @ApiProperty({ description: 'Secuencia del contenedor', example: 1 })
  sequenceId!: number;

  @ApiPropertyOptional({
    description: 'Iniciales/número del contenedor',
    example: 'MSCU1234567',
  })
  initials?: string;
}

/**
 * DTO completo para detalle de DUS
 * Combina datos de tblDus, tblDusItems, tblDusPackages y vwDusAgent
 */
export class ControlDusFullDetailDto {
  // === Información Principal DUS ===
  @ApiProperty({ description: 'ID del DUS', example: 13868 })
  id!: number;

  @ApiProperty({ description: 'ID de instrucción de embarque', example: 6220 })
  shippingInstructionId!: number;

  @ApiPropertyOptional({ description: 'Número de despacho', example: '18585' })
  numberDispatch?: string;

  @ApiPropertyOptional({ description: 'Número de DUS', example: '13011824' })
  numberDus?: string;

  @ApiPropertyOptional({ description: 'Dígito verificador DUS', example: 0 })
  dvDus?: number;

  @ApiPropertyOptional({
    description: 'DUS completo con DV',
    example: '13011824-0',
  })
  dusCompleto?: string;

  @ApiPropertyOptional({
    description: 'Número de booking',
    example: '070ISA1205214',
  })
  booking?: string;

  // === Información de Aduana ===
  @ApiPropertyOptional({ description: 'ID de aduana', example: 147 })
  customsId?: number;

  @ApiPropertyOptional({
    description: 'Nombre de aduana',
    example: 'VALPARAÍSO',
  })
  customsName?: string;

  @ApiPropertyOptional({
    description: 'Fecha de aceptación',
    example: '2025-10-01',
  })
  acceptanceDate?: string;

  @ApiPropertyOptional({
    description: 'Fecha de legalización',
    example: '2025-10-05',
  })
  legalizationDate?: string;

  // === Tipo de Operación ===
  @ApiPropertyOptional({ description: 'ID tipo de operación', example: 1 })
  operationTypeId?: number;

  @ApiPropertyOptional({
    description: 'Nombre tipo de operación',
    example: 'EXPORTACION',
  })
  operationTypeName?: string;

  // === Información del Cliente/Exportador ===
  @ApiPropertyOptional({ description: 'ID del cliente', example: 66 })
  customerId?: number;

  @ApiPropertyOptional({
    description: 'Nombre del cliente',
    example: 'EXSER LTDA',
  })
  customerName?: string;

  @ApiPropertyOptional({
    description: 'RUT del cliente',
    example: '12.345.678-9',
  })
  customerIdentification?: string;

  @ApiPropertyOptional({ description: 'Dirección del cliente' })
  customerAddress?: string;

  @ApiPropertyOptional({ description: 'Ciudad del cliente' })
  customerLocationName?: string;

  @ApiPropertyOptional({ description: 'Email del cliente' })
  customerEmail?: string;

  @ApiPropertyOptional({
    description: 'Porcentaje participación',
    example: 100,
  })
  percentage?: number;

  // === Segundo Cliente (si aplica) ===
  @ApiPropertyOptional({ description: 'ID segundo cliente' })
  customerId2?: number;

  @ApiPropertyOptional({ description: 'Nombre segundo cliente' })
  customerName2?: string;

  @ApiPropertyOptional({ description: 'RUT segundo cliente' })
  customerIdentification2?: string;

  @ApiPropertyOptional({ description: 'Porcentaje segundo cliente' })
  percentage2?: number;

  // === Consignatario ===
  @ApiPropertyOptional({ description: 'ID consignatario' })
  consigneeId?: number;

  @ApiPropertyOptional({ description: 'Nombre consignatario' })
  consigneeName?: string;

  // === Puertos ===
  @ApiPropertyOptional({ description: 'ID puerto de embarque' })
  portOfLoadingId?: number;

  @ApiPropertyOptional({
    description: 'Puerto de embarque',
    example: 'VALPARAISO',
  })
  portOfLoadingName?: string;

  @ApiPropertyOptional({ description: 'ID puerto de destino' })
  portOfDestinationId?: number;

  @ApiPropertyOptional({
    description: 'Puerto de destino',
    example: 'HELSINKI',
  })
  portOfDestinationName?: string;

  @ApiPropertyOptional({ description: 'ID país de destino' })
  countryOfDestinationId?: number;

  @ApiPropertyOptional({ description: 'País de destino', example: 'FINLANDIA' })
  countryOfDestinationName?: string;

  // === Transporte ===
  @ApiPropertyOptional({ description: 'ID tipo de carga' })
  typeOfLoadId?: number;

  @ApiPropertyOptional({ description: 'Tipo de carga' })
  typeOfLoadName?: string;

  @ApiPropertyOptional({ description: 'ID vía de transporte' })
  wayOfTransportId?: number;

  @ApiPropertyOptional({
    description: 'Vía de transporte',
    example: 'MARITIMO',
  })
  wayOfTransportName?: string;

  // === Naviera/Transportista ===
  @ApiPropertyOptional({ description: 'ID naviera' })
  carrierId?: number;

  @ApiPropertyOptional({ description: 'Nombre naviera', example: 'MSC' })
  carrierName?: string;

  @ApiPropertyOptional({ description: 'RUT naviera' })
  carrierIdentification?: string;

  @ApiPropertyOptional({ description: 'País naviera' })
  carrierCountryName?: string;

  // === Nave y Viaje ===
  @ApiPropertyOptional({
    description: 'Nombre de la nave',
    example: 'MSC MARIA',
  })
  vesselName?: string;

  @ApiPropertyOptional({ description: 'Número de viaje', example: '540' })
  voyageNumber?: string;

  @ApiPropertyOptional({ description: 'ID del viaje' })
  voyageId?: number;

  @ApiPropertyOptional({ description: 'Número de manifiesto' })
  numberManifiest?: string;

  // === Agente de Aduana (Despachador) ===
  @ApiPropertyOptional({ description: 'ID del agente de aduana' })
  agentId?: number;

  @ApiPropertyOptional({
    description: 'Nombre corto del despachador',
    example: 'JOSE LUIS SILVA',
  })
  agentShortName?: string;

  @ApiPropertyOptional({ description: 'Nombre completo del despachador' })
  agentFullName?: string;

  @ApiPropertyOptional({ description: 'RUT del despachador' })
  agentIdentification?: string;

  // === Transmisor ===
  @ApiPropertyOptional({ description: 'ID transmisor' })
  transmitterId?: number;

  @ApiPropertyOptional({ description: 'Nombre transmisor' })
  transmitterName?: string;

  @ApiPropertyOptional({ description: 'RUT transmisor' })
  transmitterIdentification?: string;

  // === Totales ===
  @ApiProperty({ description: 'Total de ítems', example: 1 })
  totalItems!: number;

  @ApiProperty({ description: 'Total de bultos', example: 1 })
  totalPackages!: number;

  @ApiProperty({ description: 'Peso total (KG)', example: 21710 })
  totalWeigth!: number;

  @ApiProperty({ description: 'Valor FOB total (USD)', example: 26650 })
  totalFOBValue!: number;

  @ApiPropertyOptional({ description: 'Valor de seguro' })
  safeValue?: number;

  @ApiPropertyOptional({ description: 'Valor de flete' })
  freightValue?: number;

  @ApiPropertyOptional({ description: 'Valor CIF total' })
  totalCIFAmount?: number;

  // === Información Comercial ===
  @ApiPropertyOptional({ description: 'ID moneda', example: 450 })
  currencyId?: number;

  @ApiPropertyOptional({ description: 'Nombre moneda', example: 'DÓLAR USA' })
  currencyName?: string;

  @ApiPropertyOptional({ description: 'ID modalidad de venta' })
  modeOfSaleId?: number;

  @ApiPropertyOptional({
    description: 'Modalidad de venta',
    example: 'BAJO COND.',
  })
  modeOfSaleName?: string;

  @ApiPropertyOptional({ description: 'ID cláusula de venta' })
  clauseOfSaleId?: number;

  @ApiPropertyOptional({
    description: 'Cláusula de venta (Incoterm)',
    example: 'FOB',
  })
  clauseOfSaleName?: string;

  @ApiPropertyOptional({ description: 'Monto cláusula de venta' })
  clauseOfSaleAmount?: number;

  @ApiPropertyOptional({ description: 'ID forma de pago' })
  methodOfPaymentId?: number;

  @ApiPropertyOptional({ description: 'Forma de pago', example: 'COB1' })
  methodOfPaymentName?: string;

  // === Otros Valores ===
  @ApiPropertyOptional({ description: 'Comisiones en el exterior' })
  commissionsAbroad?: number;

  @ApiPropertyOptional({ description: 'Otros gastos deducibles' })
  otherDeductibleExpenses?: number;

  @ApiPropertyOptional({ description: 'Valor de retorno' })
  returnValue?: number;

  // === Estado del DUS ===
  @ApiPropertyOptional({ description: 'Estado DUS', example: 'ACEPTADO' })
  statusDus?: string;

  @ApiPropertyOptional({ description: 'Estado parcial' })
  partialStatus?: string;

  @ApiPropertyOptional({ description: 'Número parcial' })
  partialNumber?: string;

  @ApiPropertyOptional({ description: 'Totales parciales' })
  partialTotals?: string;

  @ApiPropertyOptional({ description: 'ID tipo de embarque' })
  typeOfShipmentId?: number;

  // === Observaciones ===
  @ApiPropertyOptional({ description: 'Observaciones generales' })
  remarks?: string;

  // === Auditoría ===
  @ApiPropertyOptional({ description: 'Fecha de creación' })
  creationDate?: string;

  @ApiPropertyOptional({ description: 'Creado por' })
  createdBy?: string;

  @ApiPropertyOptional({ description: 'Última actualización' })
  lastUpdateDate?: string;

  @ApiPropertyOptional({ description: 'Actualizado por' })
  lastUpdateBy?: string;

  // === Items del DUS (productos) ===
  @ApiProperty({
    description: 'Lista de ítems/productos del DUS',
    type: [ControlDusItemDetailDto],
  })
  items!: ControlDusItemDetailDto[];

  // === Bultos del DUS ===
  @ApiProperty({
    description: 'Lista de bultos del DUS',
    type: [ControlDusPackageDto],
  })
  packages!: ControlDusPackageDto[];

  // === Contenedores del DUS ===
  @ApiProperty({
    description: 'Lista de contenedores del DUS',
    type: [ControlDusContainerDto],
  })
  containers!: ControlDusContainerDto[];
}

/**
 * DTO para la respuesta del detalle completo de DUS
 */
export class ControlDusFullDetailResponseDto {
  @ApiProperty({
    description: 'Indica si la operación fue exitosa',
    example: true,
  })
  success!: boolean;

  @ApiProperty({
    description: 'Datos del detalle del DUS',
    type: ControlDusFullDetailDto,
  })
  data!: ControlDusFullDetailDto;

  @ApiPropertyOptional({ description: 'Mensaje de la operación' })
  message?: string;

  @ApiProperty({
    description: 'Timestamp de la respuesta',
    example: '2025-12-31T12:00:00.000Z',
  })
  timestamp!: string;
}
